import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ShieldCheck, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { auth, saveSession } from '../api';
import { brand } from '../mock';

export default function Signup() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const inviteCode = params.get('invite');
  const [invite, setInvite] = useState(null); // { email, role, firmName, inviterName }
  const [inviteLoading, setInviteLoading] = useState(!!inviteCode);
  const [inviteError, setInviteError] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', firm: '', size: '2-5', phone: '', password: '' });

  useEffect(() => {
    if (!inviteCode) return;
    (async () => {
      try {
        const inv = await auth.getInvite(inviteCode);
        setInvite(inv);
        setForm((f) => ({ ...f, email: inv.email, firm: inv.firmName || f.firm }));
      } catch (err) {
        setInviteError(err?.response?.data?.detail || 'This invite is no longer valid.');
      } finally {
        setInviteLoading(false);
      }
    })();
  }, [inviteCode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let res;
      if (invite) {
        res = await auth.acceptInvite({ code: inviteCode, name: form.name, password: form.password });
      } else {
        res = await auth.signup(form);
      }
      saveSession(res);
      toast({
        title: invite ? `Welcome to ${invite.firmName || 'the team'}` : 'Welcome to Accu Desk',
        description: invite ? 'You are now part of the firm.' : 'Your 30-day trial has started.',
      });
      setTimeout(() => nav('/dashboard'), 500);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not create account.';
      toast({ title: 'Sign up failed', description: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfaf3] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 lg:p-12 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-black overflow-hidden grid place-items-center"><img src="/brand/accu-desk-logo.png" alt="Accu Desk" className="h-full w-full object-contain"/></div>
            <div className="font-semibold text-stone-900">{brand.name}</div>
          </div>

          {inviteLoading ? (
            <div className="flex items-center gap-2 text-stone-600"><Loader2 size={16} className="animate-spin"/> Loading invite...</div>
          ) : inviteError ? (
            <div className="rounded-lg bg-red-50 border border-red-100 p-4 text-sm text-red-900">
              {inviteError} <Link to="/signup" className="underline">Start a fresh trial instead</Link>.
            </div>
          ) : invite ? (
            <>
              <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-100 p-4">
                <div className="flex items-center gap-2 text-emerald-900 font-medium text-sm"><Mail size={14}/> You&apos;ve been invited</div>
                <div className="mt-1 text-sm text-emerald-900/80">
                  <strong>{invite.inviterName || 'Your firm'}</strong> invited you to join <strong>{invite.firmName || 'their firm'}</strong> as a <strong>{invite.role}</strong>.
                </div>
              </div>
              <h1 className="text-3xl font-serif font-semibold text-stone-900">Join {invite.firmName || 'the team'}</h1>
              <p className="mt-2 text-stone-600">Set up your account to get started.</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-serif font-semibold text-stone-900">Start your 30-day trial</h1>
              <p className="mt-2 text-stone-600">No credit card. Cancel any time.</p>
            </>
          )}

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Kruger" className="mt-1.5 h-11"/>
            </div>
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@firm.co.za" className="mt-1.5 h-11" disabled={!!invite}/>
            </div>
            {!invite && (
              <>
                <div>
                  <Label htmlFor="firm">Firm name</Label>
                  <Input id="firm" required value={form.firm} onChange={(e) => setForm({ ...form, firm: e.target.value })} placeholder="Kruger &amp; Associates" className="mt-1.5 h-11"/>
                </div>
                <div>
                  <Label>Team size</Label>
                  <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })}>
                    <SelectTrigger className="mt-1.5 h-11"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Just me</SelectItem>
                      <SelectItem value="2-5">2 &ndash; 5 people</SelectItem>
                      <SelectItem value="6-15">6 &ndash; 15 people</SelectItem>
                      <SelectItem value="16-50">16 &ndash; 50 people</SelectItem>
                      <SelectItem value="50+">50+ people</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="phone">Mobile number</Label>
                  <Input id="phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+27 71 234 5678" className="mt-1.5 h-11" data-testid="signup-phone-input"/>
                  <p className="mt-1 text-[11px] text-stone-500">So we can reach you about your subscription.</p>
                </div>
              </>
            )}
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="mt-1.5 h-11"/>
            </div>
          </div>

          <Button type="submit" disabled={busy || inviteLoading} className="mt-8 w-full h-11 bg-emerald-800 hover:bg-emerald-900 text-white">
            {busy ? <><Loader2 size={16} className="mr-2 animate-spin"/> {invite ? 'Joining...' : 'Creating account...'}</> : <>{invite ? 'Accept invite &amp; join' : 'Create account'} <ArrowRight size={16} className="ml-2"/></>}
          </Button>

          <p className="mt-5 text-xs text-stone-500 text-center">By creating an account you agree to our <Link to="/terms" className="underline hover:text-stone-800">Terms</Link> and <Link to="/privacy" className="underline hover:text-stone-800">Privacy Policy</Link>.</p>

          <p className="mt-6 text-sm text-stone-600 text-center">
            Already have an account? <Link to="/login" className="text-emerald-800 font-medium hover:text-emerald-900">Sign in</Link>
          </p>
        </form>
      </div>

      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-emerald-900 via-sky-900 to-emerald-950 text-white order-1 lg:order-2 relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl z-0"/>
        <div className="pointer-events-none absolute bottom-0 left-0 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl z-0"/>
        <Link to="/" className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-black overflow-hidden grid place-items-center"><img src="/brand/accu-desk-logo.png" alt="Accu Desk" className="h-full w-full object-contain"/></div>
          <div>
            <div className="font-semibold">{brand.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-200">{brand.tagline}</div>
          </div>
        </Link>
        <div className="max-w-md">
          <h2 className="text-4xl font-serif font-semibold leading-tight">
            {invite ? 'Every teammate. Every task. One workspace.' : 'Ready to run a calmer practice?'}
          </h2>
          <p className="mt-4 text-emerald-100 leading-relaxed">
            {invite
              ? `Once you accept, ${invite.inviterName || 'your firm'} will be able to assign tasks and share clients with you in real time.`
              : 'Join 500+ South African firms using Accu Desk to manage clients, deadlines and billing — in one place.'}
          </p>
          <ul className="mt-8 space-y-3 text-emerald-50">
            {(invite
              ? ['Shared client directory', 'Assign & track tasks together', 'Live time tracking per teammate', 'One dashboard for the whole firm']
              : ['30-day free trial', 'Import from SARS, CIPC, Sage & Xero', 'SA-based onboarding team', 'Cancel any time']
            ).map((t) => (
              <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-300"/> {t}</li>
            ))}
          </ul>
          <div className="mt-10 flex items-center gap-3 text-sm text-emerald-100">
            <ShieldCheck className="h-5 w-5 text-emerald-300"/> POPIA-aligned. Built in South Africa.
          </div>
        </div>
        <div className="text-xs text-emerald-200/80">&copy; {new Date().getFullYear()} Saber Accounting (Pty) Ltd</div>
      </div>
    </div>
  );
}
